import React, { useCallback, useRef, useEffect, useMemo } from 'react';
import { VariableSizeList } from 'react-window';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { useInfiniteScrollAlt } from '../../hooks/useInfiniteScrollAlt';
import { CardSkeletonList } from '../CardSkeleton';
import LoadingIndicator from './LoadingIndicator';
import useWindowSize from 'hooks/useWindowSize';
import LeadCard from '../LeadCard';

const CARD_GAP = 8;
const LOADING_INDICATOR_HEIGHT = 80;

const ColumnContent = ({
  columnId,
  columnStageName,
  items,
  isLoading,
  isRefetchingData,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onCardDrop,
  getCardHeight,
  setCardHeight,
  listRef,
  showCardHighlight,
}) => {
  const columnContentRef = useRef(null);
  const { height } = useWindowSize();

  // Setup auto-scroll for the column content area
  useEffect(() => {
    const contentElement = columnContentRef.current;
    if (!contentElement) return;

    const cleanup = autoScrollForElements({
      element: contentElement,
      canScroll: ({ source }) => source.data.type === 'card',
      getAllowedAxis: () => 'vertical',
    });

    return cleanup;
  }, []);

  const handleLoadMore = useCallback(() => {
    if (hasMore && !isLoadingMore) {
      return onLoadMore();
    }
    // Return resolved promise if conditions not met
    return Promise.resolve();
  }, [hasMore, isLoadingMore, onLoadMore]);

  // Memoize derived values
  const itemCount = useMemo(() => items.length + (hasMore ? 1 : 0), [items.length, hasMore]);

  const isEmpty = useMemo(() => items.length === 0 && !isLoading, [items.length, isLoading]);

  const handleItemsRendered = useInfiniteScrollAlt({
    onLoadMore: handleLoadMore,
    hasMore: hasMore || false,
    loading: isLoadingMore || false,
    itemCount: items.length,
    threshold: 5,
  });

  // Get item size for variable height list - memoized
  const getItemSize = useCallback(
    (rowIndex) => {
      if (rowIndex === items.length && hasMore) {
        return LOADING_INDICATOR_HEIGHT;
      }

      const lead = items[rowIndex];
      const height = getCardHeight(lead?.sk);
      return height + CARD_GAP;
    },
    [items, hasMore, getCardHeight]
  );

  // Render row component - properly memoized to prevent unnecessary re-renders
  const Row = useCallback(
    ({ index: rowIndex, style }) => {
      // Show loading indicator at the end
      if (rowIndex === items.length && hasMore) {
        return (
          <div style={style}>
            <LoadingIndicator />
          </div>
        );
      }

      const lead = items[rowIndex];
      if (!lead) return null;

      return (
        <div style={style}>
          <LeadCard
            item={lead}
            columnId={columnId}
            columnStageName={columnStageName}
            index={rowIndex}
            onDrop={onCardDrop}
            setCardHeight={setCardHeight}
          />
        </div>
      );
    },
    [items, columnId, columnStageName, onCardDrop, hasMore, setCardHeight]
  );

  return (
    <div
      ref={columnContentRef}
      className={`column-content thin-scrollbar ${showCardHighlight ? 'card-drag-over' : ''}`}
      style={{
        backgroundColor: showCardHighlight ? 'rgba(59, 130, 246, 0.05)' : 'transparent',
        overflow: 'auto',
      }}
    >
      {isLoading || isRefetchingData ? (
        // Show skeleton loaders during filter/search operations
        <CardSkeletonList count={3} />
      ) : isEmpty ? (
        <div className="empty-state">
          <p>Drop items here</p>
        </div>
      ) : (
        <VariableSizeList
          ref={listRef}
          height={height - 335}
          itemCount={itemCount}
          itemSize={getItemSize}
          width="100%"
          overscanCount={5}
          onItemsRendered={handleItemsRendered}
          outerRef={columnContentRef}
          estimatedItemSize={280}
          className="thin-scrollbar"
        >
          {Row}
        </VariableSizeList>
      )}
    </div>
  );
};

export default React.memo(ColumnContent, (prevProps, nextProps) => {
  return (
    prevProps.columnId === nextProps.columnId &&
    prevProps.items === nextProps.items &&
    prevProps.hasMore === nextProps.hasMore &&
    prevProps.isLoadingMore === nextProps.isLoadingMore &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.isRefetchingData === nextProps.isRefetchingData &&
    prevProps.showCardHighlight === nextProps.showCardHighlight
  );
});
